import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { withLocalePrefix } from '../../../shared/utils/translation';
import { consumer } from './cable';
import { standardRequestWithErrorFlash } from '../../../shared/utils/request';

export type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
};

export type ChatSummary = {
  id: number;
  title: string | null;
  updated_at: string;
};

type ServerMessage = {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
};

type ChatDetail = {
  id: number;
  title: string | null;
  updated_at: string;
  messages: ServerMessage[];
};

type AgentChatContextValue = {
  chats: ChatSummary[];
  activeChatId: number | null;
  loadingChat: boolean;
  messages: ChatMessage[];
  streamingContent: string;
  isStreaming: boolean;
  error: string | null;
  sendMessage: (text: string) => Promise<void>;
  loadChat: (id: number) => Promise<void>;
  startNewChat: () => void;
  refreshChats: () => Promise<void>;
  renameChat: (id: number, title: string) => Promise<boolean>;
  deleteChat: (id: number) => Promise<boolean>;
};

const STREAM_TIMEOUT_MS = 30000;
const t = withLocalePrefix('candidate_profile_redesign.agent_chat');

const AgentChatContext = createContext<AgentChatContextValue | null>(null);

type AgentChatProviderProps = {
  applicationId: number;
  children: ReactNode;
};

const toClientMessage = (m: ServerMessage): ChatMessage => ({
  id: `${m.role}-${m.id}`,
  role: m.role,
  content: m.content,
});

export const AgentChatProvider = ({
  applicationId,
  children,
}: AgentChatProviderProps) => {
  const [chats, setChats] = useState<ChatSummary[]>([]);
  const [activeChatId, setActiveChatId] = useState<number | null>(null);
  const [loadingChat, setLoadingChat] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streamingContent, setStreamingContent] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeChatIdRef = useRef<number | null>(null);
  const isStreamingRef = useRef(false);
  const subscriptionRef = useRef<ReturnType<
    typeof consumer.subscriptions.create
  > | null>(null);
  const streamTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const receivedChunkRef = useRef(false);

  const clearStreamTimeout = useCallback(() => {
    if (streamTimeoutRef.current) {
      clearTimeout(streamTimeoutRef.current);
      streamTimeoutRef.current = null;
    }
  }, []);

  const unsubscribe = useCallback(() => {
    subscriptionRef.current?.unsubscribe();
    subscriptionRef.current = null;
  }, []);

  const subscribe = useCallback(
    (chatId: number) => {
      unsubscribe();

      subscriptionRef.current = consumer.subscriptions.create(
        {
          channel: 'Agents::Channels::ChatChannel',
          chat_id: chatId,
        },
        {
          received(data: {
            type: string;
            content?: string;
            message_id?: number;
            message?: string;
            title?: string;
          }) {
            switch (data.type) {
              case 'chunk':
                receivedChunkRef.current = true;
                clearStreamTimeout();
                setStreamingContent((prev) => prev + (data.content ?? ''));
                break;
              case 'message_break':
                setStreamingContent((prev) => {
                  if (prev) {
                    setMessages((msgs) => [
                      ...msgs,
                      {
                        id: `assistant-${Date.now()}-${msgs.length}`,
                        role: 'assistant',
                        content: prev,
                      },
                    ]);
                  }
                  return '';
                });
                break;
              case 'done':
                clearStreamTimeout();
                setStreamingContent((prev) => {
                  if (prev) {
                    setMessages((msgs) => [
                      ...msgs,
                      {
                        id: `assistant-${data.message_id ?? Date.now()}`,
                        role: 'assistant',
                        content: prev,
                      },
                    ]);
                  }
                  return '';
                });
                setIsStreaming(false);
                isStreamingRef.current = false;
                break;
              case 'title_updated':
                if (data.title) {
                  const newTitle = data.title;
                  setChats((prev) =>
                    prev.map((c) =>
                      c.id === chatId ? { ...c, title: newTitle } : c
                    )
                  );
                }
                break;
              case 'error':
                clearStreamTimeout();
                setError(data.message ?? t('error_default'));
                setStreamingContent('');
                setIsStreaming(false);
                isStreamingRef.current = false;
                break;
            }
          },
        }
      );
    },
    [clearStreamTimeout, unsubscribe]
  );

  const loadChat = useCallback(
    async (chatId: number) => {
      setLoadingChat(true);
      try {
        const response = await standardRequestWithErrorFlash.get<ChatDetail>(
          `/candidate_agent/chats/${chatId}`,
          { params: { application_id: applicationId } }
        );
        const detail = response.data;
        setMessages(detail.messages.map(toClientMessage));
        setStreamingContent('');
        setError(null);
        setActiveChatId(detail.id);
        activeChatIdRef.current = detail.id;
        subscribe(detail.id);
      } catch {
        setError(t('error_load_failed'));
      } finally {
        setLoadingChat(false);
      }
    },
    [applicationId, subscribe]
  );

  // Teardown only runs when the profile unmounts (page navigation/reload).
  useEffect(() => {
    return () => {
      unsubscribe();
      clearStreamTimeout();
    };
  }, [clearStreamTimeout, unsubscribe]);

  const startNewChat = useCallback(() => {
    if (isStreamingRef.current) {
      return;
    }
    unsubscribe();
    clearStreamTimeout();
    setMessages([]);
    setStreamingContent('');
    setError(null);
    setActiveChatId(null);
    activeChatIdRef.current = null;
  }, [clearStreamTimeout, unsubscribe]);

  const refreshChats = useCallback(async () => {
    try {
      const response = await standardRequestWithErrorFlash.get<ChatSummary[]>(
        '/candidate_agent/chats',
        { params: { application_id: applicationId } }
      );
      setChats(response.data);
    } catch {
      // Silent — the menu will show whatever we last fetched.
    }
  }, [applicationId]);

  const renameChat = useCallback(
    async (id: number, title: string): Promise<boolean> => {
      try {
        const response = await standardRequestWithErrorFlash.patch<ChatSummary>(
          `/candidate_agent/chats/${id}`,
          { title, application_id: applicationId }
        );
        const updated = response.data;
        setChats((prev) =>
          prev.map((c) =>
            c.id === id
              ? { ...c, title: updated.title, updated_at: updated.updated_at }
              : c
          )
        );
        return true;
      } catch {
        return false;
      }
    },
    [applicationId]
  );

  const deleteChat = useCallback(
    async (id: number): Promise<boolean> => {
      try {
        await standardRequestWithErrorFlash.delete(
          `/candidate_agent/chats/${id}`,
          { params: { application_id: applicationId } }
        );
        setChats((prev) => prev.filter((c) => c.id !== id));
        if (activeChatIdRef.current === id) {
          unsubscribe();
          clearStreamTimeout();
          setMessages([]);
          setStreamingContent('');
          setError(null);
          setActiveChatId(null);
          activeChatIdRef.current = null;
          setIsStreaming(false);
          isStreamingRef.current = false;
        }
        return true;
      } catch {
        return false;
      }
    },
    [applicationId, clearStreamTimeout, unsubscribe]
  );

  const sendMessage = useCallback(
    async (text: string) => {
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: text,
      };
      setMessages((prev) => [...prev, userMessage]);
      setIsStreaming(true);
      isStreamingRef.current = true;
      setError(null);
      setStreamingContent('');
      receivedChunkRef.current = false;

      const priorChatId = activeChatIdRef.current;

      try {
        const response = await standardRequestWithErrorFlash.post<{
          chat_id: number;
        }>('/candidate_agent/chats', {
          message: text,
          application_id: applicationId,
          chat_id: priorChatId,
        });

        const newChatId = response.data.chat_id;
        const isNewChat = priorChatId !== newChatId;

        if (isNewChat) {
          activeChatIdRef.current = newChatId;
          setActiveChatId(newChatId);
          subscribe(newChatId);

          if (priorChatId === null) {
            setChats((prev) => {
              if (prev.some((c) => c.id === newChatId)) {
                return prev;
              }
              return [
                {
                  id: newChatId,
                  title: null,
                  updated_at: new Date().toISOString(),
                },
                ...prev,
              ];
            });
          }
        }

        streamTimeoutRef.current = setTimeout(() => {
          if (!receivedChunkRef.current) {
            setError(t('error_timeout'));
            setStreamingContent('');
            setIsStreaming(false);
            isStreamingRef.current = false;
          }
        }, STREAM_TIMEOUT_MS);
      } catch {
        setError(t('error_send_failed'));
        setIsStreaming(false);
        isStreamingRef.current = false;
      }
    },
    [applicationId, subscribe]
  );

  return (
    <AgentChatContext.Provider
      value={{
        chats,
        activeChatId,
        loadingChat,
        messages,
        streamingContent,
        isStreaming,
        error,
        sendMessage,
        loadChat,
        startNewChat,
        refreshChats,
        renameChat,
        deleteChat,
      }}
    >
      {children}
    </AgentChatContext.Provider>
  );
};

export const useAgentChat = (): AgentChatContextValue => {
  const ctx = useContext(AgentChatContext);
  if (!ctx) {
    throw new Error('useAgentChat must be used within AgentChatProvider');
  }
  return ctx;
};
